import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FaShoppingCart, FaChevronLeft, FaChevronRight, FaMinus, FaPlus } from "react-icons/fa";
import StarRating from "../../components/StarRating";
import { useCart } from "../../hooks/useCart";
import { useToastContext } from "../../context/ToastContext";
import { formatPrice, getDiscountPercent } from "../../utils/formatPrice";
import products from "../../data/products";
import categories from "../../data/categories";
import styles from "./ProductDetailPage.module.css";

function ProductDetailPage() {
  const { id } = useParams();
  const { addItem } = useCart();
  const { addToast } = useToastContext();

  const product = products.find(p => p.id === id);
  const [imgIndex, setImgIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);

  if (!product) {
    return (
      <div className={styles.notFound}>
        <h2>找不到此商品</h2>
        <Link to="/products" className={styles.backLink}>← 返回商品列表</Link>
      </div>
    );
  }

  const displayPrice = product.salePrice !== null ? product.salePrice : product.price;
  const discount = getDiscountPercent(product);
  const category = categories.find(c => c.id === product.category);
  const related = products
    .filter(p => p.category === product.category && p.id !== product.id)
    .slice(0, 4);

  function handleAddToCart() {
    addItem(product, quantity);
    addToast(`「${product.name}」已加入購物車`, "success");
  }

  function prevImg() {
    setImgIndex(i => (i === 0 ? product.images.length - 1 : i - 1));
  }

  function nextImg() {
    setImgIndex(i => (i === product.images.length - 1 ? 0 : i + 1));
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Breadcrumb */}
        <nav className={styles.breadcrumb} aria-label="麵包屑">
          <Link to="/">首頁</Link>
          <span>/</span>
          <Link to="/products">商品</Link>
          <span>/</span>
          {category && (
            <>
              <Link to={`/products?category=${category.id}`}>{category.name}</Link>
              <span>/</span>
            </>
          )}
          <span className={styles.breadcrumbCurrent}>{product.name}</span>
        </nav>

        <div className={styles.detail}>
          {/* Image Gallery */}
          <div className={styles.gallery}>
            <div className={styles.mainImage}>
              <img
                src={product.images[imgIndex]}
                alt={`${product.name} 圖片 ${imgIndex + 1}`}
                className={styles.mainImg}
              />
              {product.images.length > 1 && (
                <>
                  <button onClick={prevImg} className={styles.imgNavBtn} aria-label="上一張">
                    <FaChevronLeft />
                  </button>
                  <button onClick={nextImg} className={`${styles.imgNavBtn} ${styles.imgNavRight}`} aria-label="下一張">
                    <FaChevronRight />
                  </button>
                </>
              )}
              {discount && <span className={styles.badge}>-{discount}%</span>}
            </div>
            <div className={styles.thumbs}>
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setImgIndex(i)}
                  className={`${styles.thumb} ${i === imgIndex ? styles.thumbActive : ""}`}
                  aria-label={`圖片 ${i + 1}`}
                >
                  <img src={img} alt={`${product.name} 縮圖 ${i + 1}`} />
                </button>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div className={styles.info}>
            {category && (
              <Link to={`/products?category=${category.id}`} className={styles.categoryTag}>
                {category.name}
              </Link>
            )}
            <h1 className={styles.name}>{product.name}</h1>
            <StarRating rating={product.rating} reviewCount={product.reviewCount} size={16} />

            <div className={styles.priceBlock}>
              <span className={styles.price}>{formatPrice(displayPrice)}</span>
              {product.salePrice !== null && (
                <span className={styles.originalPrice}>{formatPrice(product.price)}</span>
              )}
            </div>

            <p className={styles.description}>{product.description}</p>

            <div className={styles.stockRow}>
              <span className={product.stock > 0 ? styles.inStock : styles.outStock}>
                {product.stock > 0 ? `庫存 ${product.stock} 件` : "已售完"}
              </span>
            </div>

            {product.stock > 0 && (
              <div className={styles.quantityRow}>
                <span className={styles.quantityLabel}>數量</span>
                <div className={styles.quantityControl}>
                  <button
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    className={styles.qtyBtn}
                    aria-label="減少數量"
                  >
                    <FaMinus />
                  </button>
                  <span className={styles.qtyValue}>{quantity}</span>
                  <button
                    onClick={() => setQuantity(q => Math.min(product.stock, q + 1))}
                    className={styles.qtyBtn}
                    aria-label="增加數量"
                  >
                    <FaPlus />
                  </button>
                </div>
              </div>
            )}

            <button
              className={styles.addBtn}
              onClick={handleAddToCart}
              disabled={product.stock === 0}
              aria-label={`加入 ${product.name} 至購物車`}
            >
              <FaShoppingCart />
              {product.stock === 0 ? "已售完" : "加入購物車"}
            </button>

            {product.tags.length > 0 && (
              <div className={styles.tags}>
                {product.tags.map(tag => (
                  <span key={tag} className={styles.tag}>{tag}</span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {related.length > 0 && (
          <section className={styles.related}>
            <h2 className={styles.relatedTitle}>同分類商品</h2>
            <div className={styles.relatedGrid}>
              {related.map(p => (
                <Link key={p.id} to={`/products/${p.id}`} className={styles.relatedCard}>
                  <img src={p.images[0]} alt={p.name} className={styles.relatedImg} />
                  <div className={styles.relatedInfo}>
                    <p className={styles.relatedName}>{p.name}</p>
                    <p className={styles.relatedPrice}>
                      {formatPrice(p.salePrice !== null ? p.salePrice : p.price)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default ProductDetailPage;
